import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import mixpanel from "mixpanel-browser";
import { toast } from "react-toastify";
import ContentContainer from "../../../../templates/content-container/contentContainer";
import ContentContainerToolbar from "../../../../ui-components/molecules/content-container-toolbar/contentContainerToolbar";
import Button, { ButtonSizes, ButtonThemes } from "../../../../ui-components/button/button";
import Tag from "../../../../ui-components/molecules/tag/tag";
import ProgressBar from "../../../../ui-components/progress-bar/progressBar";
import { PAGE_IDS } from "../../../../app/data/common/pageIds";
import { QUERY_KEYS } from "../../../../app/data/common/queryKeys";
import { Routes } from "../../../../app/route/RoutesConfig";
import InvoiceFactoringService, { BatchFileKind } from "../../../../app/data/invoice-factoring/invoiceFactoringService";
import {
  ACTIVE_BATCH_STATUSES,
  BATCH_STATUS_COLORS,
  BATCH_STATUS_LABELS,
  FACTORING_POLL_MS,
  FACTORING_QUERY_OPTIONS,
  formatDateTime,
} from "../constants";
import { getBatchNextAction, getProgressPercent, getRemainingCount } from "../utils/nextAction";
import { InvoiceFactoringPath, InvoiceFactoringPathParams } from "../route";
import { useFactoringContext } from "../common/useFactoringContext";
import { downloadBlob } from "../common/downloadFile";
import NextActionChip from "../common/nextActionChip";
import ActivityList from "../common/activityList";
import BatchActions from "./batchActions";
import BatchItemsTable from "./batchItemsTable";
import "../../../../ui-components/tabs/tabs.scss";
import "../invoiceFactoring.scss";

const invoiceFactoringService = InvoiceFactoringService.getInstance();

const FactoringBatchDetails: React.FC = () => {
  const history = useHistory();
  const params = useParams() as InvoiceFactoringPathParams;
  const batchId = params[InvoiceFactoringPath.batchId] as string;
  const queryClient = useQueryClient();
  const { canManage, terminalPrefix } = useFactoringContext();
  const [downloading, setDownloading] = useState<BatchFileKind | null>(null);

  useEffect(() => {
    mixpanel.track("page_viewed", { page: "invoice_factoring_batch_details" });
  }, []);

  const { data: batch, isLoading, isError, error } = useQuery({
    queryKey: [QUERY_KEYS.INVOICE_FACTORING_BATCH, batchId],
    queryFn: () => invoiceFactoringService.getBatch(batchId),
    retry: false,
    refetchInterval: (data) => (data && ACTIVE_BATCH_STATUSES.includes(data.status) ? FACTORING_POLL_MS : false),
    ...FACTORING_QUERY_OPTIONS,
  });

  const isActive = !!batch && ACTIVE_BATCH_STATUSES.includes(batch.status);
  const pollInterval = isActive ? FACTORING_POLL_MS : false;

  const { data: activity, isLoading: activityLoading } = useQuery({
    queryKey: [QUERY_KEYS.INVOICE_FACTORING_BATCH_ACTIVITY, batchId],
    queryFn: () => invoiceFactoringService.getBatchActivity(batchId),
    retry: false,
    refetchInterval: pollInterval,
    ...FACTORING_QUERY_OPTIONS,
  });

  const refreshAll = () => {
    queryClient.invalidateQueries([QUERY_KEYS.INVOICE_FACTORING_BATCH, batchId]);
    queryClient.invalidateQueries([QUERY_KEYS.INVOICE_FACTORING_BATCH_ITEMS, batchId]);
    queryClient.invalidateQueries([QUERY_KEYS.INVOICE_FACTORING_BATCH_ACTIVITY, batchId]);
    queryClient.invalidateQueries([QUERY_KEYS.INVOICE_FACTORING_BATCHES]);
    queryClient.invalidateQueries([QUERY_KEYS.INVOICE_FACTORING_STATS]);
  };

  const onDownload = async (kind: BatchFileKind) => {
    setDownloading(kind);
    try {
      const { blob, fileName } = await invoiceFactoringService.downloadBatchFile(batchId, kind);
      downloadBlob(blob, fileName);
    } catch (e) {
      toast.error((e as Error).message || "Unable to download the file.");
    } finally {
      setDownloading(null);
    }
  };

  const title = batch ? `Batch #${batch.batchNumber}` : "Batch Details";

  return (
    <ContentContainer
      className="xgs-list xgs-list--full-width"
      mods={{ "full-width": true }}
      pageId={PAGE_IDS.INVOICE_FACTORING_BATCH_DETAILS}
      isLoading={isLoading}
      titleComponent={
        <ContentContainerToolbar pageId={PAGE_IDS.INVOICE_FACTORING_BATCH_DETAILS} title={title} mods={{ "full-width": true }}>
          <Button
            theme={ButtonThemes.blueInverted}
            size={ButtonSizes.auto}
            type="button"
            onClick={() => history.push(`${terminalPrefix}${Routes.billing.invoiceFactoringBatches}`)}
          >
            All batches
          </Button>
          {batch && (
            <>
              <Button
                theme={ButtonThemes.blueInverted}
                size={ButtonSizes.auto}
                type="button"
                spinner={downloading === "source"}
                disabled={!!downloading}
                onClick={() => onDownload("source")}
                title="Download the CSV file as it was uploaded"
              >
                Uploaded CSV
              </Button>
              <Button
                theme={ButtonThemes.blueInverted}
                size={ButtonSizes.auto}
                type="button"
                spinner={downloading === "report"}
                disabled={!!downloading}
                onClick={() => onDownload("report")}
                title="Download one row per probill with job and document results"
              >
                Results report
              </Button>
            </>
          )}
        </ContentContainerToolbar>
      }
    >
      <div className="xgs-invoice-factoring">
        {isError && (
          <div className="xgs-invoice-factoring__error-box">Unable to load the batch. {(error as Error)?.message}</div>
        )}

        {batch && (
          <>
            <div className="xgs-invoice-factoring__card">
              <div className="xgs-invoice-factoring__card-head">
                <div className="xgs-invoice-factoring__card-title">
                  <span>{batch.name}</span>
                  <Tag mods={{ color: BATCH_STATUS_COLORS[batch.status], uppercase: false }}>
                    {BATCH_STATUS_LABELS[batch.status]}
                  </Tag>
                  <NextActionChip action={getBatchNextAction(batch)} />
                </div>
                {canManage && <BatchActions batch={batch} onChanged={refreshAll} />}
              </div>

              <ProgressBar
                percentage={getProgressPercent(batch)}
                additionalInfo={`${batch.processedCount} of ${batch.totalCount - batch.invalidCount} probills processed`}
              />

              <div className="xgs-invoice-factoring__stats">
                <div><span>Rows in file</span><strong>{batch.totalCount}</strong></div>
                <div><span>Sent</span><strong>{batch.succeededCount}</strong></div>
                <div><span>Already sent</span><strong>{batch.skippedCount}</strong></div>
                <div>
                  <span>Failed</span>
                  <strong className={batch.failedCount ? "xgs-invoice-factoring__text--error" : undefined}>
                    {batch.failedCount}
                  </strong>
                </div>
                <div><span>Invalid or duplicate</span><strong>{batch.invalidCount}</strong></div>
                <div><span>Waiting</span><strong>{getRemainingCount(batch)}</strong></div>
              </div>

              {batch.lastError && (
                <div className="xgs-invoice-factoring__error-box">Last error: {batch.lastError}</div>
              )}

              <dl className="xgs-invoice-factoring__meta">
                <dt>File</dt>
                <dd>{batch.fileName}</dd>
                <dt>Uploaded</dt>
                <dd>{formatDateTime(batch.createdAt)} by {batch.createdBy}</dd>
                <dt>Started</dt>
                <dd>{batch.startedAt ? `${formatDateTime(batch.startedAt)} by ${batch.startedBy}` : "—"}</dd>
                {batch.stoppedAt && (
                  <>
                    <dt>Stopped</dt>
                    <dd>{formatDateTime(batch.stoppedAt)} by {batch.stoppedBy}</dd>
                  </>
                )}
                <dt>Finished</dt>
                <dd>{formatDateTime(batch.completedAt)}</dd>
                {isActive && (
                  <>
                    <dt>Updates</dt>
                    <dd>Refreshing every {FACTORING_POLL_MS / 1000} seconds while the batch is active</dd>
                  </>
                )}
              </dl>
            </div>

            <Tabs>
              <TabList>
                <Tab>Probills</Tab>
                <Tab>Activity</Tab>
              </TabList>
              <TabPanel>
                <BatchItemsTable
                  batchId={batchId}
                  itemsByStatus={batch.itemsByStatus}
                  terminalPrefix={terminalPrefix}
                  refetchInterval={pollInterval}
                />
              </TabPanel>
              <TabPanel>
                <ActivityList entries={activity} isLoading={activityLoading} />
              </TabPanel>
            </Tabs>
          </>
        )}
      </div>
    </ContentContainer>
  );
};

export default FactoringBatchDetails;
